import {WORLD_UNITS_PER_METER} from './world-scale.js';

function freezeData(value){
 for(const child of Object.values(value))if(child&&typeof child==='object')freezeData(child);
 return Object.freeze(value);
}

/** Adult male display exemplars, not a female-response or synchrony model.
 * Sources establish the flash interval, duration, color and upward J-like flight.
 * The envelope, path dimensions and dark return are bounded engineering choices.
 * Park-local clock times are deliberately not treated as a universal schedule.
 */
export const FIREFLY_DISPLAY_PROFILE=freezeData({
 id:'photinus-pyralis-adult-male-display-v1',version:1,
 species:'Photinus pyralis',lifeStage:'adult',sex:'male',color:'yellow',
 timing:{periodSeconds:[5,7],durationSeconds:[.6,.8]},
 engineering:{
  riseFraction:.22,fallFraction:.28,hookFraction:.45,darkReturnSeconds:1.25,
  verticalRiseMeters:.12,horizontalHookMeters:.045,
  verticalRiseWorldUnits:.12*WORLD_UNITS_PER_METER,
  horizontalHookWorldUnits:.045*WORLD_UNITS_PER_METER,
 },
 environment:{duskCenter:.5,duskThreshold:.24,duskGain:5,rainAttenuation:.7},
 sources:[
  {url:'https://mynpn.usanpn.org/npnapps/species/Photinus/pyralis',checked:'2026-10-11',supports:'Male single yellow flash: 0.6–0.8 seconds, repeated every 5–7 seconds.'},
  {url:'https://home.nps.gov/grsm/learn/nature/firefly-flash-patterns.htm',checked:'2026-10-11',supports:'Male flies upward during one sustained yellow flash, often tracing a J; starts before full dark.'},
 ],
});

const clamp01=n=>Math.max(0,Math.min(1,n));
const finiteOr=(value,fallback)=>Number.isFinite(value)?value:fallback;
// Quintic smoothstep has zero first and second derivatives at both ends.
function smooth(value){const t=clamp01(value);return t*t*t*(t*(t*6-15)+10);}
function smoothDerivative(t){return t<=0||t>=1?0:30*t*t*(t-1)*(t-1);}
function hashUnit(text){
 let h=2166136261;
 for(let i=0;i<text.length;i++){h^=text.charCodeAt(i);h=Math.imul(h,16777619);}
 h^=h>>>16;h=Math.imul(h,0x7feb352d);h^=h>>>15;h=Math.imul(h,0x846ca68b);h^=h>>>16;
 return(h>>>0)/4294967296;
}
function identity(value,name){
 if(typeof value==='string'||typeof value==='number'&&Number.isFinite(value))return value;
 throw new TypeError(`${name} must be a finite number or string`);
}

/** Create once per resident (or recreate identically after a reset).
 * Every parameter uses its own keyed deterministic hash. Nothing is advanced
 * while sampling, and neither wall time nor Math.random participates.
 */
export function createFireflyDisplay(sceneSeed=1945,residentId=0){
 const seed=identity(sceneSeed,'sceneSeed'),id=identity(residentId,'residentId');
 const key=JSON.stringify([FIREFLY_DISPLAY_PROFILE.id,seed,id]);
 const periodSeconds=5+2*hashUnit(`${key}:period`);
 const angle=2*Math.PI*hashUnit(`${key}:heading`);
 return Object.freeze({
  profileId:FIREFLY_DISPLAY_PROFILE.id,sceneSeed:seed,residentId:id,
  periodSeconds,durationSeconds:.6+.2*hashUnit(`${key}:duration`),
  phaseSeconds:periodSeconds*hashUnit(`${key}:phase`),
  horizontalDirectionX:Math.cos(angle),horizontalDirectionZ:Math.sin(angle),
 });
}

/** Preserve the scene's existing dusk/winter/rain visibility policy.
 * This is not a light pulse: a resident remains visible through its dark phase.
 * State fields are read each call, including when the simulation is paused.
 */
export function fireflyBodyVisibility(state={}){
 const p=FIREFLY_DISPLAY_PROFILE.environment;
 const winter=clamp01(finiteOr(state.seasonMix?.[3],0));
 const sun=clamp01(finiteOr(state.sun,.635)),rain=clamp01(finiteOr(state.rain,0));
 return(1-winter)*clamp01((Math.abs(sun-p.duskCenter)-p.duskThreshold)*p.duskGain)*(1-rain*p.rainAttenuation);
}

/** Sample only absolute simulation state.time (seconds), never animalTime.
 * forwardOffset/upOffset and forwardVelocity/upVelocity describe the local
 * display phrase in world units and world units/second. offsetX/Y/Z and
 * velocityX/Y/Z rotate that phrase into its seeded horizontal direction.
 * These additive offsets/velocities exclude the caller's baseline orbit.
 * flashIntensity (also displayIntensity) already contains the environment gate.
 * The caller must apply terrain/water/case clearance to the FINAL position.
 * Reusing out is optional; the individual and input state are never modified.
 */
export function sampleFireflyDisplay(individual,state={},out={}){
 const time=finiteOr(state.time,0),period=individual.periodSeconds,duration=individual.durationSeconds;
 const remainder=(time+individual.phaseSeconds)%period;
 const cycleSeconds=remainder<0?remainder+period:remainder;
 const e=FIREFLY_DISPLAY_PROFILE.engineering;
 let pulse=0,horizontal=0,vertical=0,horizontalVelocity=0,verticalVelocity=0;
 if(cycleSeconds<duration){
  const u=cycleSeconds/duration;
  pulse=u<e.riseFraction?smooth(u/e.riseFraction):u>1-e.fallFraction?smooth((1-u)/e.fallFraction):1;
  // A rounded horizontal hook leads into the upward stem during the flash.
  // Both axes have zero boundary velocity; the rise is monotone while lit.
  horizontal=smooth(u/e.hookFraction);
  horizontalVelocity=smoothDerivative(u/e.hookFraction)/(e.hookFraction*duration);
  const rise=smooth(u);vertical=rise*rise;verticalVelocity=2*rise*smoothDerivative(u)/duration;
 }else if(cycleSeconds<duration+e.darkReturnSeconds){
  // Settle back to baseline only after emission is completely dark.
  horizontal=vertical=1-smooth((cycleSeconds-duration)/e.darkReturnSeconds);
  horizontalVelocity=verticalVelocity=-smoothDerivative((cycleSeconds-duration)/e.darkReturnSeconds)/e.darkReturnSeconds;
 }
 const bodyVisibility=fireflyBodyVisibility(state);
 out.cycleSeconds=cycleSeconds;out.pulse=pulse;
 out.bodyVisibility=bodyVisibility;out.flashIntensity=out.displayIntensity=bodyVisibility*pulse;
 out.forwardOffset=horizontal*e.horizontalHookWorldUnits*bodyVisibility;
 out.upOffset=vertical*e.verticalRiseWorldUnits*bodyVisibility;
 out.forwardVelocity=horizontalVelocity*e.horizontalHookWorldUnits*bodyVisibility;
 out.upVelocity=verticalVelocity*e.verticalRiseWorldUnits*bodyVisibility;
 out.offsetX=out.forwardOffset*individual.horizontalDirectionX;
 out.offsetY=out.upOffset;out.offsetZ=out.forwardOffset*individual.horizontalDirectionZ;
 out.velocityX=out.forwardVelocity*individual.horizontalDirectionX;
 out.velocityY=out.upVelocity;out.velocityZ=out.forwardVelocity*individual.horizontalDirectionZ;
 return out;
}
